/**
 * Brand context — the seam for Decision #21 (fully white-label reseller
 * experience). Every customer-facing surface should read its name/logo/colors/
 * support contacts from here, never from a hardcoded "Possignin"/"ITCity"
 * literal. Default is the platform brand; reseller shells wrap their subtree
 * with the reseller's brand so the same components render white-labelled.
 *
 * Wiring this with the default changes nothing visually — it's the foundation
 * the 198-literal sweep (PROJECT_ASSESSMENT.md §4F) migrates onto.
 */
import { createContext, useContext, useLayoutEffect, type ReactNode } from 'react';

export type Brand = {
  /** Full display name, e.g. "Possignin" or a reseller's "Acme POS". */
  name: string;
  /** Short label for tight spaces. */
  shortName: string;
  /** Primary accent (hex). */
  primaryColor: string;
  supportEmail: string;
  supportPhone: string | null;
  /** Optional logo asset URL; null → use the geometric <Logo/> mark. */
  logoUrl: string | null;
  /** Browser-tab icon (uploaded via Branding / Platform settings); null → the bundled default. */
  faviconUrl: string | null;
  /**
   * Apex domain for customer-facing URLs (subdomains, status page, CNAME target).
   * Platform → "itcitysoft.com"; a white-label reseller → their own, e.g. "acmepos.com".
   * Lets the same chrome render `<sub>.{brand.domain}` without leaking ITCity on a
   * reseller's customer path (Decision #21 / SECURITY_COMPLIANCE_CHECKLIST §22).
   */
  domain: string;
  /**
   * True for the ITCity/Possignin platform host; false for a white-label reseller
   * host. Drives host-aware routing (ITCity company page vs. reseller landing) and
   * lets components keep ITCity copy only when this is the platform.
   */
  isPlatform: boolean;
  /** DB deployment modes a signup on this host may pick (reseller policy; platform = all 3). */
  allowedDbModes: string[];
};

export const PLATFORM_BRAND: Brand = {
  name: 'Possignin',
  shortName: 'Possignin',
  primaryColor: '#4F46E5',
  supportEmail: 'support@itcitysoft.com',
  supportPhone: null,
  logoUrl: null,
  faviconUrl: null,
  domain: 'itcitysoft.com',
  isPlatform: true,
  allowedDbModes: ['shared', 'managed', 'self_hosted'],
};

const BrandContext = createContext<Brand>(PLATFORM_BRAND);

export function BrandProvider({ brand = PLATFORM_BRAND, children }: { brand?: Brand; children: ReactNode }) {
  // Drive the brand accent from the resolved brand. The Tailwind `bg-brand` /
  // `text-brand` utilities read `--color-brand`, so overriding it on <html> makes
  // every accent follow the reseller's primaryColor (Decision #21). Platform
  // brand keeps the default indigo, so the platform host is unchanged.
  useLayoutEffect(() => {
    const root = document.documentElement;
    if (brand.isPlatform) {
      root.style.removeProperty('--color-brand');
    } else {
      root.style.setProperty('--color-brand', brand.primaryColor);
    }
    // White-label the browser-tab title too — otherwise the static index.html
    // "Possignin" <title> leaks on a reseller host.
    document.title = brand.name;
    // And the tab icon: an uploaded favicon (reseller Branding / admin Platform
    // settings) replaces the bundled default; unset keeps index.html's link.
    if (brand.faviconUrl) {
      let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.removeAttribute('type'); // uploaded icons are png/ico, not the default svg
      link.href = brand.faviconUrl;
    }
  }, [brand.isPlatform, brand.primaryColor, brand.name, brand.faviconUrl]);

  return <BrandContext.Provider value={brand}>{children}</BrandContext.Provider>;
}

/** Read the active brand. Defaults to the platform brand outside any provider. */
export function useBrand(): Brand {
  return useContext(BrandContext);
}
